import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  ViewRef,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatTableDataSource } from '@angular/material/table';
import { RouterLink } from '@angular/router';
import { CmsService, Page, PaginationModel, TranslatePipe, TranslationService } from '@spartacus/core';
import { BREAKPOINT, BreakpointService } from '@spartacus/storefront';
import { CellService, ColumnConfig } from 'material-dynamic-table';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { CustomTranslationService } from 'src/app/service/language/custom-translation.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import {
  GetManagementEmployeesPayload,
  ManagementEmployeeService,
} from 'src/app/service/myaccount/management-employee.service';
import {
  MANAGE_EMPLOYEES,
  MyaccountFeaturesConfigService,
} from 'src/app/service/myaccount/myaccount-features-config.service';
import { ADDRESS_NAME, SnahCountryService } from 'src/app/service/snah/snah-countries.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { PaginationComponent } from '../../pagination/pagination.component';
import { SortingComponent } from '../../sorting/sorting.component';
import { TableComponent } from '../../table/table.component';
import { EmployeeAddListsComponent } from './employee-add-lists/employee-add-lists.component';
import { EmployeeDeliveryAddressListComponent } from './employee-delivery-address-list/employee-delivery-address-list.component';
import { EmployeeStatusListsComponent } from './employee-status-lists/employee-status-lists.component';

@Component({
  selector: 'app-myaccount-employee-lists',
  templateUrl: './myaccount-employee-lists.component.html',
  styleUrls: ['./myaccount-employee-lists.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    SortingComponent,
    RouterLink,
    MatIcon,
    TableComponent,
    NgClass,
    PaginationComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class MyaccountEmployeeListsComponent implements OnInit, OnDestroy {
  managementEmployees$: Observable<any>;
  itemCount: number;
  isDesktop = false;
  csDesktopWidth = this.breakpointService.getSize(BREAKPOINT.lg);
  managementEmployeesPayload: GetManagementEmployeesPayload = {
    currentPage: 0,
    pageSize: 10,
    sort: 'byLastNameAsc',
  };
  PaginationModel: PaginationModel = {
    currentPage: 0,
    pageSize: 0,
    sort: null,
    totalPages: 0,
    totalResults: 0,
  };
  columns: ColumnConfig[] = [
    {
      name: 'firstName',
      displayName: 'First Name',
      type: 'string',
      sort: false,
    },
    {
      name: 'lastName',
      displayName: 'Last Name',
      type: 'string',
      sort: false,
    },
    {
      name: 'email',
      displayName: 'Email Address',
      type: 'string',
      sort: false,
    },

    {
      name: 'deliveryAddress',
      displayName: 'Delivery Address',
      type: 'html',
      sort: false,
    },
    {
      name: 'active',
      displayName: 'Status',
      type: 'active',
      sort: false,
    },
    {
      name: '',
      type: 'actions',
      sort: false,
    },
  ];
  headerNameSubscription: Subscription;
  dataSource = new MatTableDataSource<any>();
  sort = 'byLastNameAsc';

  sortOptions = [
    {
      code: 'byStatus',
      selected: false,
    },
    {
      code: 'byLastNameAsc',
      selected: false,
    },
    {
      code: 'byLastNameDesc',
      selected: false,
    },
  ];
  sortLabels: Observable<{
    byStatus: string;
    byLastNameAsc: string;
    byLastNameDesc: string;
  }>;

  getB2BMgmtEeSubscription: Subscription;
  activeCountry$: Observable<string> = this.countryService.getActive();
  activeLanguage$: Observable<string> = this.languageService.getActive();

  isCustomTranslationsEnabled$: Observable<boolean> = combineLatest([this.activeCountry$, this.activeLanguage$]).pipe(
    switchMap(([activeCountry, activeLanguage]) =>
      this.customTranslationService.isCustomTranslationsEnabled$(activeCountry, activeLanguage, [
        'myAccount.sortLabels.lastNameDesc',
      ])
    )
  );

  isManageEmployeesEnabled$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.myaccountFeaturesConfigSerivce.isMyaccountFeatureEnabled$(activeCountry, MANAGE_EMPLOYEES)
    )
  );
  showAddressName$: Observable<boolean> = this.snahCountryService.getSnahFeatures(ADDRESS_NAME);

  constructor(
    private managementEmployeeService: ManagementEmployeeService,
    protected cms: CmsService,
    private cdr: ChangeDetectorRef,
    private readonly cellService: CellService,
    private breakpointService: BreakpointService,
    private translation: TranslationService,
    private languageService: ShimanoLanguageService,
    private countryService: ShimanoCountryService,
    private customTranslationService: CustomTranslationService,
    private myaccountFeaturesConfigSerivce: MyaccountFeaturesConfigService,
    private snahCountryService: SnahCountryService,
    private storageService: StorageService
  ) {
    // DO NOT CHANGE THE ORDER
    const columnTranslations = ['firstName', 'lastName', 'emailAddress', 'deliveryAddress', 'status'];
    columnTranslations.forEach((translate, index) => {
      this.headerNameSubscription = this.translation
        .translate(`myAccount.${translate}`)
        .subscribe((col) => (this.columns[index].displayName = col));
    });

    this.cellService.registerCell('html', EmployeeDeliveryAddressListComponent);
    this.cellService.registerCell('active', EmployeeStatusListsComponent);
    this.cellService.registerCell('actions', EmployeeAddListsComponent);
  }

  ngOnInit() {
    this.isDesktop = window.innerWidth >= this.csDesktopWidth;
    this.storageService.removeSessionStorageItem('status');
    this.storageService.removeSessionStorageItem('lastname');
    this.storageService.removeSessionStorageItem('firstname');
    this.storageService.removeSessionStorageItem('email');
    this.storageService.removeSessionStorageItem('jobtitle');
    this.storageService.removeSessionStorageItem('address');
    this.storageService.removeSessionStorageItem('formattedadd');
    this.storageService.removeSessionStorageItem('useruid');
    this.storageService.removeSessionStorageItem('storeFrontPermissions');
    this.storageService.removeSessionStorageItem('shipToAddresses');
    this.getB2BManagementEmployees(this.managementEmployeesPayload);
  }

  private getB2BManagementEmployees(payload: any) {
    this.getB2BMgmtEeSubscription = this.isCustomTranslationsEnabled$
      .pipe(
        switchMap((isCustomTranslationsEnabled) => {
          this.sortLabels = this.managementEmployeeService.getSortLabels(isCustomTranslationsEnabled);
          return this.cms.getCurrentPage();
        }),
        filter(Boolean)
      )
      .subscribe((data: Page) => {
        if (data.label === '/my-account') {
          this.managementEmployees$ = this.managementEmployeeService.getManagementEmployees(payload);
          this.managementEmployees$.pipe(take(1)).subscribe((items) => {
            if (items?.entries) {
              this.itemCount = items.entries.length;
              if (items.entries.length > 0) {
                this.dataSource = items.entries.map((tableData: any) => ({ ...tableData }));
                this.updatePaginationModel(items.pagination);
                if (!(this.cdr as ViewRef).destroyed) {
                  this.cdr.detectChanges();
                }
              }
            }
          });
        }
      });
  }

  sortChange(sort: string) {
    this.sort = sort;
    this.managementEmployeesPayload.sort = sort;
    this.getB2BManagementEmployees(this.managementEmployeesPayload);
  }

  private updatePaginationModel = (result: any) => {
    this.PaginationModel = {
      currentPage: result.currentPage,
      pageSize: result.pageSize,
      sort: result.sort,
      totalPages: result.totalPages,
      totalResults: result.totalResults,
    };
  };

  pageChange(page: any) {
    this.managementEmployeesPayload.currentPage = page;
    this.getB2BManagementEmployees(this.managementEmployeesPayload);
  }

  onClickEdit(
    status: any,
    lastName: string,
    firstName: string,
    email: string,
    jobTitle: string,
    defaultAddress: string,
    formattedAddress: string,
    uid: string,
    accountPermissions: any
  ) {
    this.storageService.setSessionStorageItem('status', status);
    this.storageService.setSessionStorageItem('lastname', lastName);
    this.storageService.setSessionStorageItem('firstname', firstName);
    this.storageService.setSessionStorageItem('email', email);
    this.storageService.setSessionStorageItem('jobtitle', jobTitle);
    this.storageService.setSessionStorageItem('address', defaultAddress);
    this.storageService.setSessionStorageItem('formattedadd', formattedAddress);
    this.storageService.setSessionStorageItem('useruid', uid);
    this.storageService.setSessionStorageItem('storeFrontPermissions', accountPermissions);
  }

  @HostListener('window:resize')
  onResize() {
    this.isDesktop = window.innerWidth >= this.csDesktopWidth;
  }

  ngOnDestroy() {
    if (this.headerNameSubscription) {
      this.headerNameSubscription.unsubscribe();
    }
    if (this.getB2BMgmtEeSubscription) {
      this.getB2BMgmtEeSubscription.unsubscribe();
    }
  }
}
